/**
 * BPA Solutions Smart Forms - API Layer
 * Handles both Mock and Live (Google Apps Script) modes.
 */

const GAS_URL = import.meta.env.VITE_GAS_WEB_APP_URL;

const getContext = () => ({
  origin: window.location.origin,
  siteDomain: window.location.hostname,
  pageUrl: window.location.href,
  referrer: document.referrer,
  userAgent: navigator.userAgent,
  clientId: "default"
});

const callBackend = async (payload) => {
  if (!GAS_URL) {
    return mockFallback(payload);
  }

  try {
    const body = new URLSearchParams();
    Object.keys(payload).forEach(key => {
      const value = payload[key];
      body.append(key, (value === null || value === undefined) ? '' : value);
    });

    const response = await fetch(GAS_URL, {
      method: 'POST',
      body: body
    });

    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }

    const text = await response.text();
    let result;
    try {
      result = JSON.parse(text);
    } catch (e) {
      if (text && text.toLowerCase().includes('success')) {
        return { success: true, ok: true, message: text };
      }
      throw new Error(text || 'The backend returned an invalid response format.');
    }

    if (result.error) throw new Error(result.error);
    if (result.ok === false) throw new Error(result.message || 'Submission failed.');

    return {
      ok: result.ok,
      message: result.message,
      ...(result.data || {})
    };
  } catch (error) {
    console.error(`[API ERROR] Action: ${payload.action}`, error);
    throw error;
  }
};

const mockFallback = async (payload) => {
  await new Promise(r => setTimeout(r, 1200));
  switch (payload.action) {
    case 'request_code':
    case 'resend_code':
      if (!payload.email?.includes('@')) throw new Error('Invalid email address');
      return {
        success: true,
        ok: true,
        verificationId: payload.verificationId || `v_${Math.random().toString(36).slice(2, 9)}`,
        sessionToken: `tk_pre_${Math.random().toString(36).slice(2, 10)}`,
        expiresAt: new Date(Date.now() + 600000).toISOString()
      };
    case 'verify_code':
      if (/^\d{6}$/.test(payload.code)) {
        return {
          success: true,
          ok: true,
          sessionToken: `tk_full_${Math.random().toString(36).slice(2, 15)}`,
          verifiedAt: new Date().toISOString()
        };
      }
      throw new Error('Invalid code. Try any 6 digits.');
    case 'submit_form':
      return {
        success: true,
        ok: true,
        submissionId: `BPA-${Math.floor(Math.random() * 1000000)}`,
        submittedAt: new Date().toISOString()
      };
    default:
      throw new Error('Unknown action');
  }
};

export const mockApi = {
  sendVerificationCode: async (email, formType, category) => {
    return callBackend({
      action: "request_code",
      email,
      formType,
      category,
      ...getContext()
    });
  },
  resendCode: async (email, verificationId) => {
    return callBackend({
      action: "resend_code",
      email,
      verificationId,
      ...getContext()
    });
  },
  verifyCode: async (email, code, verificationId) => {
    return callBackend({
      action: "verify_code",
      email,
      code,
      verificationId,
      userAgent: navigator.userAgent,
      clientId: "default"
    });
  },
  submitForm: async (payload) => {
    return callBackend({
      action: "submit_form",
      clientId: "default",
      ...getContext(),
      ...payload
    });
  }
};